// ФТУЕ.2: оверлей-движок туториала. Один корневой компонент
// <TutorialOverlay /> подключается в App.tsx; он подписывается на
// useTutorialStore.step и рендерит:
//
//   1. SVG-маску затемнения (спотлайт) — чёрная c прозрачной "дыркой"
//      вокруг таргета.
//   2. Неоновую рамку вокруг дырки (дышит 1.6s).
//   3. Drag-стрелку — пунктирная линия от source к target.
//   4. Палец-указатель — SVG, анимация tap 1.2s.
//   5. Coach-mark — тёмный бабл со стрелкой к таргету (или в центре).
//   6. Progress bar вверху — «N / TOTAL» + кнопка SKIP.
//
// Координаты считаются по getBoundingClientRect() для targetSel/dragToSel.
// Перечитываются на каждый rAF (raf-loop), чтобы оверлей не отставал
// при resize/scroll/анимациях DOM. Это дешевле и надёжнее ResizeObserver
// для редко-меняющихся таргетов туториала.

import { useEffect, useRef } from 'react';
import {
  useTutorialStore, type CoachDir, type TutorialStep
} from './store';
import s from './tutorial.module.css';

const ACCENT_MAP: Record<NonNullable<TutorialStep['accent']>, string> = {
  cy: '#3DF0FF',
  mg: '#FF3D8F',
  amber: '#FFB13D',
  lime: '#9CFF3D',
  deus: '#8FB2FF',
  syn:  '#C93DFF',
  gsec: '#FFB13D',
  liv:  '#3DFFA5'
};

export function TutorialOverlay() {
  const step       = useTutorialStore(z => z.step);
  const targetRect = useTutorialStore(z => z.targetRect);
  const dragToRect = useTutorialStore(z => z.dragToRect);
  const onNextCb   = useTutorialStore(z => z.onNextCb);
  const onSkipCb   = useTutorialStore(z => z.onSkipCb);
  const setTargetRect = useTutorialStore(z => z.setTargetRect);
  const setDragToRect = useTutorialStore(z => z.setDragToRect);
  const stop          = useTutorialStore(z => z.stop);

  const rafRef = useRef<number | null>(null);

  // rAF-loop: пересчитывает targetRect/dragToRect каждый кадр пока активен
  // шаг с таргетом. Дешёвый getBoundingClientRect (~0.05ms на элемент).
  useEffect(() => {
    if (!step?.targetSel && !step?.dragToSel) {
      setTargetRect(null);
      setDragToRect(null);
      return;
    }
    let last1 = '';
    let last2 = '';
    const tick = () => {
      if (step.targetSel) {
        const el = document.querySelector(step.targetSel);
        if (el) {
          const r = el.getBoundingClientRect();
          const key = `${r.x}|${r.y}|${r.width}|${r.height}`;
          if (key !== last1) { last1 = key; setTargetRect(r); }
        } else if (last1 !== '') {
          last1 = ''; setTargetRect(null);
        }
      }
      if (step.dragToSel) {
        const el2 = document.querySelector(step.dragToSel);
        if (el2) {
          const r = el2.getBoundingClientRect();
          const key = `${r.x}|${r.y}|${r.width}|${r.height}`;
          if (key !== last2) { last2 = key; setDragToRect(r); }
        } else if (last2 !== '') {
          last2 = ''; setDragToRect(null);
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [step?.targetSel, step?.dragToSel, setTargetRect, setDragToRect]);

  if (!step) return null;

  const accent = ACCENT_MAP[step.accent || 'cy'];

  // Точка центра таргета для палец/drag.
  const targetCx = targetRect ? targetRect.x + targetRect.width / 2 : null;
  const targetCy = targetRect ? targetRect.y + targetRect.height / 2 : null;

  // Тап на target → advance (если разрешено).
  const handleSpotlightClick = (e: React.MouseEvent) => {
    if (!targetRect || !step.advanceOnTargetClick) {
      // Без advanceOnTargetClick клик гасится (пользователь должен жать «Дальше»).
      e.stopPropagation();
      return;
    }
    // Если клик попал в "дырку" — позволяем элементу под ней отработать.
    const x = e.clientX, y = e.clientY;
    const inHole = x >= targetRect.x && x <= targetRect.x + targetRect.width
                && y >= targetRect.y && y <= targetRect.y + targetRect.height;
    if (inHole) {
      // дальше прокинется на сам target — а после render-цикла сценарий
      // (вызывающий код) переключит шаг через onNextCb.
      onNextCb?.();
    } else {
      e.stopPropagation();
    }
  };

  const handleSkip = () => {
    if (onSkipCb) onSkipCb();
    else stop();
  };
  const handleNext = () => {
    if (onNextCb) onNextCb();
    else stop();
  };

  return (
    <div className={s.host} style={{ ['--tt-accent' as any]: accent }}>
      {/* === Прогресс-бар вверху === */}
      {step.progress && (
        <ProgressBar
          current={step.progress.current}
          total={step.progress.total}
          onSkip={handleSkip}
          showSkip={step.coach?.showSkip !== false}
        />
      )}

      {/* === Спотлайт-маска === */}
      {step.spotlight && targetRect && (
        <Spotlight rect={targetRect} accent={accent} onClick={handleSpotlightClick} />
      )}

      {/* === Pulse-рамка без спотлайта (на случай если не нужно затемнение) === */}
      {step.pulse && !step.spotlight && targetRect && (
        <PulseRing rect={targetRect} accent={accent} />
      )}

      {/* === Drag-стрелка === */}
      {step.dragToSel && targetRect && dragToRect && (
        <DragArrow from={targetRect} to={dragToRect} accent={accent} />
      )}

      {/* === Палец-указатель === */}
      {step.hand && targetCx != null && targetCy != null && targetRect && (
        <HandPointer x={targetCx} y={targetRect.y + targetRect.height + 16} accent={accent} />
      )}

      {/* === Coach-mark === */}
      {step.coach && (
        <CoachMark
          coach={step.coach}
          rect={targetRect}
          onNext={handleNext}
          onSkip={handleSkip}
        />
      )}
    </div>
  );
}

// ============================================================
// Spotlight (SVG-маска с дыркой) + Neon ring
// ============================================================
function Spotlight({
  rect, accent, onClick
}: { rect: DOMRect; accent: string; onClick: (e: React.MouseEvent) => void }) {
  const PAD = 8; // отступ дырки от bounding rect
  const R = 12;  // border-radius дырки
  const x = rect.x - PAD;
  const y = rect.y - PAD;
  const w = rect.width + PAD * 2;
  const h = rect.height + PAD * 2;

  return (
    <>
      <svg className={s.spotSvg} onClick={onClick}>
        <defs>
          <mask id="tt-spot-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect x={x} y={y} width={w} height={h} rx={R} ry={R} fill="black" />
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.78)" mask="url(#tt-spot-mask)" />
      </svg>
      <div
        className={s.spotRing}
        style={{
          left: x, top: y, width: w, height: h,
          ['--tt-accent' as any]: accent
        }}
      />
    </>
  );
}

function PulseRing({ rect, accent }: { rect: DOMRect; accent: string }) {
  const PAD = 6;
  return (
    <div
      className={s.spotRing}
      style={{
        left: rect.x - PAD,
        top: rect.y - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
        ['--tt-accent' as any]: accent
      }}
    />
  );
}

// ============================================================
// Hand pointer (SVG-палец, анимация tap)
// ============================================================
function HandPointer({ x, y, accent }: { x: number; y: number; accent: string }) {
  return (
    <svg
      className={s.hand}
      style={{ left: x, top: y }}
      viewBox="0 0 40 48"
      fill="none"
    >
      <path
        d="M14 18 V8 a4 4 0 0 1 8 0 v18 l4-3 a3 3 0 0 1 5 3 l-3 12 a6 6 0 0 1-6 4 h-6 a6 6 0 0 1-6-5 l-2-10 a3 3 0 0 1 5-2 l1 1 z"
        fill="#0a0a14"
        stroke={accent}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="20" cy="36" r="3" fill={accent} opacity="0.6" />
    </svg>
  );
}

// ============================================================
// Drag arrow (пунктирная стрелка)
// ============================================================
function DragArrow({
  from, to, accent
}: { from: DOMRect; to: DOMRect; accent: string }) {
  const x1 = from.x + from.width / 2;
  const y1 = from.y + from.height / 2;
  const x2 = to.x + to.width / 2;
  const y2 = to.y + to.height / 2;

  // Стрелка-наконечник: треугольник, повёрнутый по углу линии.
  const angle = Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI;
  const HEAD = 10;

  return (
    <svg className={s.dragSvg} style={{ ['--tt-accent' as any]: accent }}>
      <line className={s.dragLine} x1={x1} y1={y1} x2={x2} y2={y2} />
      <polygon
        className={s.dragHead}
        points={`0,-${HEAD/2} ${HEAD},0 0,${HEAD/2}`}
        transform={`translate(${x2}, ${y2}) rotate(${angle})`}
      />
    </svg>
  );
}

// ============================================================
// Coach-mark (бабл со стрелкой)
// ============================================================
function CoachMark({
  coach, rect, onNext, onSkip
}: {
  coach: NonNullable<TutorialStep['coach']>;
  rect: DOMRect | null;
  onNext: () => void;
  onSkip: () => void;
}) {
  // Когда таргета нет — coach рендерится как «центральная плашка»
  // без позиционирования относительно элемента. Inset 16px по краям,
  // вертикально по центру через flex родителя. Не уйдёт за экран.
  if (!rect) {
    return (
      <div className={`${s.coach} ${s.coachCentered}`}>
        {coach.title && <div className={s.coachTitle}>{coach.title}</div>}
        <p className={s.coachBody}>{coach.body}</p>
        {(coach.showNext !== false || coach.showSkip !== false) && (
          <div className={s.coachRow}>
            {coach.showSkip !== false
              ? <button className={s.coachSkip} onClick={onSkip}>пропустить</button>
              : <span />}
            {coach.showNext !== false
              ? <button className={s.coachNext} onClick={onNext}>
                  {coach.nextLabel || 'Дальше →'}
                </button>
              : <span />}
          </div>
        )}
      </div>
    );
  }

  // Coach со стрелкой — позиционирование относительно таргета.
  const dir: CoachDir = coach.dir || autoDir(rect);
  const pos = computeCoachPos(rect, dir);
  const arrowSide = oppositeSide(dir);

  return (
    <div
      className={s.coach}
      style={{
        left: pos.left,
        top: pos.top,
        transform: pos.transform
      }}
    >
      <span className={`${s.coachArrow} ${s[arrowSide]}`} />
      {coach.title && <div className={s.coachTitle}>{coach.title}</div>}
      <p className={s.coachBody}>{coach.body}</p>
      {(coach.showNext !== false || coach.showSkip !== false) && (
        <div className={s.coachRow}>
          {coach.showSkip !== false
            ? <button className={s.coachSkip} onClick={onSkip}>пропустить</button>
            : <span />}
          {coach.showNext !== false
            ? <button className={s.coachNext} onClick={onNext}>
                {coach.nextLabel || 'Дальше →'}
              </button>
            : <span />}
        </div>
      )}
    </div>
  );
}

function autoDir(rect: DOMRect): CoachDir {
  // Если таргет в верхней половине — coach внизу; иначе сверху.
  const vh = window.innerHeight;
  return rect.y + rect.height / 2 < vh / 2 ? 'bottom' : 'top';
}

function oppositeSide(dir: CoachDir): 'top' | 'bottom' | 'left' | 'right' {
  if (dir === 'top' || dir === 'top-left' || dir === 'top-right') return 'bottom';
  if (dir === 'bottom' || dir === 'bottom-left' || dir === 'bottom-right') return 'top';
  if (dir === 'left') return 'right';
  return 'left';
}

function computeCoachPos(rect: DOMRect | null, dir: CoachDir): {
  left: number; top: number; transform: string;
} {
  if (!rect) {
    // Без таргета — coach в центре экрана.
    return {
      left: window.innerWidth / 2,
      top: window.innerHeight / 2,
      transform: 'translate(-50%, -50%)'
    };
  }
  const cx = rect.x + rect.width / 2;
  const PAD = 18;

  if (dir.startsWith('top')) {
    return { left: cx, top: rect.y - PAD, transform: 'translate(-50%, -100%)' };
  }
  if (dir.startsWith('bottom')) {
    return { left: cx, top: rect.y + rect.height + PAD, transform: 'translate(-50%, 0)' };
  }
  if (dir === 'left') {
    return { left: rect.x - PAD, top: rect.y + rect.height / 2, transform: 'translate(-100%, -50%)' };
  }
  // right
  return { left: rect.x + rect.width + PAD, top: rect.y + rect.height / 2, transform: 'translate(0, -50%)' };
}

// ============================================================
// Progress bar
// ============================================================
function ProgressBar({
  current, total, onSkip, showSkip
}: { current: number; total: number; onSkip: () => void; showSkip: boolean }) {
  const pct = Math.max(0, Math.min(100, (current / total) * 100));
  return (
    <div className={s.progress}>
      <span className={s.progressLabel}>FTUE · {current}/{total}</span>
      <div className={s.progressTrack}>
        <div className={s.progressFill} style={{ width: `${pct}%` }} />
      </div>
      {showSkip && (
        <button className={s.progressSkip} onClick={onSkip}>SKIP</button>
      )}
    </div>
  );
}
